/**
 * Drag-&-Drop Pure-Helpers (06/2026 Refactor Phase 3).
 *
 * Reine, deterministische Helper-Funktionen für das Kalender-Drag-and-Drop.
 * Kein State, kein setX. Die React-Komponente verarbeitet die Events und ruft
 * diese Helper für die Berechnung der neuen Slot-Position auf.
 */

const AUTOSCROLL_EDGE_THRESHOLD_PX = 80;
const AUTOSCROLL_SPEED_PX = 8;
const AUTOSCROLL_TICK_MS = 16;

/**
 * Berechnet aus dem Drag-Event + Slot-Label die neue Slot-Position mit
 * 15-Minuten-Präzision (obere/untere Slot-Hälfte).
 *
 * @returns {{ adjustedTimeSlot: string, minutesOffset: 0|15 }}
 */
export const computeAdjustedSlot = (event, baseTimeSlot) => {
  const rect = event.currentTarget?.getBoundingClientRect?.();
  const halfHeight = rect ? rect.height / 2 : 0;
  const relativeY = rect ? event.clientY - rect.top : 0;

  if (rect && relativeY > halfHeight) {
    const [h, m] = baseTimeSlot.split(':').map(Number);
    const newM = m + 15;
    return {
      adjustedTimeSlot: `${h.toString().padStart(2, '0')}:${newM.toString().padStart(2, '0')}`,
      minutesOffset: 15,
    };
  }
  return { adjustedTimeSlot: baseTimeSlot, minutesOffset: 0 };
};

/**
 * Berechnet die neuen Start-/End-Zeiten eines verschobenen Termins basierend
 * auf dem Drop-Slot und der ursprünglichen Dauer.
 *
 * @returns {{ newStartTime: Date, newEndTime: Date }}
 */
export const computeDropTimes = (event, baseTimeSlot, currentDate, appointment) => {
  let [hours, minutes] = baseTimeSlot.split(':').map(Number);
  const { minutesOffset } = computeAdjustedSlot(event, baseTimeSlot);
  minutes += minutesOffset;

  const newStartTime = new Date(currentDate);
  newStartTime.setHours(hours, minutes, 0, 0);

  const originalStart = new Date(appointment.start_time);
  const originalEnd = new Date(appointment.end_time);
  const duration = originalEnd - originalStart;
  const newEndTime = new Date(newStartTime.getTime() + duration);

  return { newStartTime, newEndTime };
};

/**
 * 06/2026 Drop-Präzisions-Fix: Berechnet die Ziel-Zeit direkt aus der
 * absoluten Cursor-Y-Position relativ zum Kalender-Grid — unabhängig davon,
 * welches DOM-Element das Event abgefangen hat. Das behebt den Bug, bei dem
 * ein Drop auf einen bestehenden Termin (dessen Block als absolut
 * positioniertes Kind der 06:00-Zelle gerendert wird) fälschlich auf 06:00
 * landete bzw. "mittig" einrastete.
 *
 * @returns {{ newStartTime: Date, newEndTime: Date, label: string, snappedMinutes: number } | null}
 */
export const computeDropFromGrid = ({
  clientY,
  gridRect,
  slotHeight,
  calendarStartMinutes,
  calendarEndMinutes,
  durationMinutes,
  currentDate,
  snapMinutes = 5,
}) => {
  if (!gridRect || !slotHeight) return null;
  const pxPerMin = slotHeight / 30;
  const rawMin = calendarStartMinutes + (clientY - gridRect.top) / pxPerMin;
  let snapped = Math.round(rawMin / snapMinutes) * snapMinutes;
  const maxStart = Math.max(calendarStartMinutes, calendarEndMinutes - durationMinutes);
  snapped = Math.max(calendarStartMinutes, Math.min(snapped, maxStart));
  const h = Math.floor(snapped / 60);
  const m = snapped % 60;
  const newStartTime = new Date(currentDate);
  newStartTime.setHours(h, m, 0, 0);
  const newEndTime = new Date(newStartTime.getTime() + durationMinutes * 60000);
  const label = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  return { newStartTime, newEndTime, label, snappedMinutes: snapped };
};

/**
 * Prüft ob ein Termin nur in Mindbody existiert und daher LOKAL nicht
 * verschoben werden darf. Solche Termine haben IDs wie `appointment:NNN`
 * oder `class:NNN` ODER `source === 'mindbody'`.
 */
export const isMindbodyOnlyAppointment = (appointment) => {
  if (!appointment) return false;
  const id = String(appointment.id || '');
  return (
    id.startsWith('appointment:') ||
    id.startsWith('class:') ||
    appointment.source === 'mindbody'
  );
};

/**
 * Startet/aktualisiert Auto-Scroll wenn der Cursor nahe an der Viewport-Kante
 * ist. Liefert einen Cleanup-Identifier (Interval-Handle oder null).
 *
 * Caller muss den vorherigen Handle in `clearInterval` löschen UND vor jedem
 * Aufruf den alten Handle zurücksetzen.
 */
export const startEdgeAutoScroll = (clientY) => {
  const viewportHeight = window.innerHeight;
  if (clientY > viewportHeight - AUTOSCROLL_EDGE_THRESHOLD_PX) {
    return window.setInterval(
      () => window.scrollBy(0, AUTOSCROLL_SPEED_PX),
      AUTOSCROLL_TICK_MS
    );
  }
  if (clientY < AUTOSCROLL_EDGE_THRESHOLD_PX) {
    return window.setInterval(
      () => window.scrollBy(0, -AUTOSCROLL_SPEED_PX),
      AUTOSCROLL_TICK_MS
    );
  }
  return null;
};

/**
 * Heuristik: Wieviele Plätze belegt eine Buchung (für Multi-Participant-
 * Services wie 1:2/1:3 wichtig).
 */
export const computeOccupancy = (appointment) =>
  appointment?.customer_ids?.length || 1;

/**
 * Helper: Lese `max_participants` aus dem Service-Dict.
 */
export const getMaxParticipants = (services, serviceId) => {
  const svc = services.find((s) => s.id === serviceId);
  return svc?.max_participants || 1;
};
